import { FocusMonitor } from '@angular/cdk/a11y';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  ElementRef,
  forwardRef,
  inject,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  Self,
  ViewChild
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ControlValueAccessor, UntypedFormControl, NgControl, NG_VALUE_ACCESSOR, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { MatFormField, MatFormFieldControl, MAT_FORM_FIELD } from '@angular/material/form-field';
import { Subject } from 'rxjs';

@Component({
  standalone:true,
  imports:[CommonModule,FormsModule,ReactiveFormsModule],
  selector: 'app-textarea',
  templateUrl: './textarea.component.html',
  styleUrls: ['./textarea.component.scss'],
  providers: [{
    provide: MatFormFieldControl,
    useExisting: TextareaComponent
  }]
})
export class TextareaComponent implements OnInit, OnDestroy, ControlValueAccessor {
  destroyRef = inject(DestroyRef)
  static nextId = 0;

  @ViewChild('textAreaElement') textAreaElement: ElementRef;
  @Input() rows: number = 6;
  @Input() maxLength: number = 128;

  @Input()
  get value(): any {
    return this.control.value;
  }
  set value(value: any) {
    this.control.setValue(value);
    this.stateChanges.next();
  }
  public control: UntypedFormControl = new UntypedFormControl(null);

  @Input()
  get placeholder() {
    return this._placeholder;
  }
  set placeholder(placeholder) {
    this._placeholder = placeholder;
    this.stateChanges.next();
  }
  public _placeholder: string;

  @Input()
  get required(): boolean {
    return this._required;
  }
  set required(value: boolean) {
    this._required = coerceBooleanProperty(value);
    this.stateChanges.next();
  }
  private _required = false;

  @Input()
  get disabled(): boolean {
    return this._disabled;
  }
  set disabled(value: boolean) {
    this._disabled = coerceBooleanProperty(value);
    this._disabled ? this.control.disable() : this.control.enable();
    this.stateChanges.next();
  }
  private _disabled = false;

  get empty() {
    return !this.control.value;
  }

  get shouldLabelFloat() {
    return this.focused || !this.empty;
  }

  get errorState(): boolean {
    return this.ngControl.invalid && this.ngControl.touched;
  }

  public focused = false;

  public id = `textarea-component-${TextareaComponent.nextId++}`;

  private onChangeCallback = (value: any) => {};
  private onTouchedCallback = () => {};


  public stateChanges = new Subject<void>();

  constructor(
    private _focusMonitor: FocusMonitor,
    private _elementRef: ElementRef<HTMLElement>,
    @Optional() @Inject(MAT_FORM_FIELD) public _formField: MatFormField,
    @Optional() @Self() public ngControl: NgControl
  ) {
    this._focusMonitor.monitor(this._elementRef, true).subscribe(origin => {
      if (this.focused && !origin) {
        this.onTouchedCallback();
      }
      this.focused = !!origin;
      this.stateChanges.next();
    });

    // Replace the provider from above with this.
    if (this.ngControl != null) {
      // Setting the value accessor directly (instead of using
      // the providers) to avoid running into a circular import.
      this.ngControl.valueAccessor = this;
    }
  }

  ngOnInit(): void {
    this.control.valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(value => {
      try {
        this.onChangeCallback(value);
      } catch(e) { }
    });
  }

  ngOnDestroy(): void {
    this.stateChanges.complete();
  }

  registerOnChange(fn: any) {
    this.onChangeCallback = fn;
  }

  registerOnTouched(fn: () => {}): void {
    this.onTouchedCallback = fn;
  }

  writeValue(value: string) {
    this.control.setValue(value);
  }

  setDescribedByIds(ids: string[]) {
    const controlElement = this._elementRef.nativeElement
      .querySelector('.app-textarea-container')!;
    controlElement.setAttribute('aria-describedby', ids.join(' '));
  }

  onContainerClick(event: MouseEvent) {
    if ((event.target as Element).tagName.toLowerCase() != 'textarea') {
      this._elementRef.nativeElement.querySelector('textarea').focus();
    }
  }

}
